import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { ConfigService } from './app-config.service';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { LayoutService } from './layout/service/app.layout.service';
import { Observable, Subject, combineLatest, filter, first, takeUntil, withLatestFrom } from 'rxjs';
import { UserAction, UserState, UserStatus } from '@state/user.state';
import { Select, Store } from '@ngxs/store';
import { UserClaims } from './@types/dto/user-claims.model';
import { AuthenticationService } from './auth/service';
import { QuestionnaireAction } from '@state/questionnaire.state';
import { CookieConsentService } from '@services/common/cookie.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LoaderService } from '@services/loader/loader.service';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { OrganizationAction } from '@state/organization.state';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OnboardingComponent } from './main/apps/components/onboarding/onboarding.component';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { GoogleTagManagerService } from '@services/common/google-tag-manager.service';
import { User } from './auth/models';
import { TermsComponent } from './main/apps/components/terms/terms.component';
import { QuestionnaireConfigAction } from '@state/questionnaire.config.state';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [DialogService],
})
export class AppComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;
    @Select(UserState.getUserStatus) userStatus$!: Observable<UserStatus>;
    @Select(UserState.getCurrentUser) user$: Observable<User>;

    private ngUnsubscribe = new Subject<void>();
    public userClaim: UserClaims;

    //Onboarding
    public isOrgOnboarded: boolean = false;
    public ref: DynamicDialogRef | undefined;

    constructor(
        private primengConfig: PrimeNGConfig,
        private configService: ConfigService,
        private cookieConsentService: CookieConsentService,
        private gtmService: GoogleTagManagerService,
        private storageService: StorageService,
        private layoutService: LayoutService,
        private translateService: CustomTranslateService,
        private authenticationService: AuthenticationService,
        private store: Store,
        private titleService: Title,
        private router: Router,
        private loader: LoaderService,
        private questionnaireService: QuestionnaireHttpService,
        public dialogService: DialogService,
    ) {
        // Global option for all charts
    }

    ngOnInit(): void {
        if (this.configService.getConfig.googleTagManager.id.length > 0) {
            this.gtmService.activate();
        }

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationStart || e instanceof NavigationEnd),
                takeUntil(this.ngUnsubscribe),
                withLatestFrom(this.user$),
            )
            .subscribe(([e, user]) => {
                this.loader.setLoading(e instanceof NavigationStart);
                if (this.userClaim && this.userClaim.organization) {
                    this.isOrgOnboarded = this.userClaim.organization.isOnboarded ?? false;
                    const currentRoute = this.router.url;
                    if (!this.isOrgOnboarded && this.ref === undefined && user.has_accepted_terms === '1') {
                        this.openOnboardingModal();
                    }
                }
            });

        this.primengConfig.ripple = true; //enables core ripple functionality

        this.translateService.get('common.title').subscribe((title) => {
            if (title) {
                this.titleService.setTitle(title as string);
            }
        });

        const layoutconfig: any = this.storageService.getGlobalItem(LocalStorageKeys.layoutConfig);

        this.authenticationService.currentUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            if (user) {
                this.store.dispatch(new UserAction.Activate());
            }
        });

        this.userClaims$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((claims) => {
            this.userClaim = claims;
            this.handleUserClaims();
        });

        if (this.configService.getConfig.isChatbotShown) {
            Chathive.widget.init({
                apiKey: this.configService.getConfig.chatHiveAPI,
                language: this.translateService.currentLang,
                hide: false,
            });
        }
        this.cookieConsentService.updateCookiePopup();
    }

    handleUserClaims() {
        if (this.userClaim && this.userClaim.organization && this.userClaim.organization_entities) {
            if (this.userClaim.organization?.uid) {
                this.store.dispatch(new OrganizationAction.Activate(this.userClaim.organization.uid));
                this.questionnaireService
                    .getAll(this.userClaim.organization?.uid)
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe((questionnairesQuestionInstances) => {
                        const questionnaires = questionnairesQuestionInstances
                            .map(({ questionnaire }) => questionnaire)
                            .filter((quest) => !quest.deleted);
                        const sortedquestionaires = questionnaires.sort(
                            (a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
                        );
                        this.store.dispatch(
                            new QuestionnaireAction.Activate(
                                this.userClaim.organization?.uid!,
                                sortedquestionaires[0].firstYearMeasured,
                                sortedquestionaires[0].entityId,
                            ),
                        );
                        this.store.dispatch(
                            new QuestionnaireConfigAction.Activate(
                                this.userClaim.organization?.uid!,
                                sortedquestionaires[0].entityId,
                            ),
                        );
                    });
            }
        }
    }

    private openOnboardingModal() {
        const element = document.getElementById('language-selector');
        if (element) {
            element.style.display = 'none';
        }

        this.ref = this.dialogService.open(OnboardingComponent, {
            width: '1200px',
            height: '80vh',
            closable: false,
            styleClass: 'onboarding-modal',
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
